/* eslint-disable react/no-unused-class-component-methods */
import * as palette from "./palette";

import {
  Airtable,
  Divider,
  GoogleForms,
  GoogleSpreadsheet,
  Image,
  Typeform,
  YouTubeVideo,
  Newsletter,
  GovlaunchProject,
} from "./blocks";
import {
  BlockquoteStyle,
  FigureResetStyle,
  H3H4ParagraphStyle,
  H3Style,
  H4Style,
  ParagraphStyle,
  PlaceholderStyle,
  ResetHeadingStyle,
} from "./EditorBlockStyles";
import {
  CompositeDecorator,
  Editor,
  EditorState,
  Modifier,
  RichUtils,
  SelectionState,
  genKey as generateRandomKey,
  getDefaultKeyBinding,
} from "draft-js";
import { Mention, MentionSpanDecoratorConfig } from "./mentions";
import React, { Component } from "react";
import { callRegisteredEvents, getRegisteredEvents } from "./OnEditorEvent";
import { findEntitiesByType, resetBlockStyle, shouldDisplayPlaceholder } from "./utils";

import AtomicBlockIdentifiers from "./AtomicBlockIdentifiers";
import EditorAreaFocusState from "./EditorAreaFocusState";
import EntityTypeIdentifiers from "./EntityTypeIdentifiers";
import { Link } from "./entities";
import PropTypes from "prop-types";
import classNames from "classnames";
import { css } from "@emotion/css";
import testId from "./testId";
import Simplecast from "./blocks/Simplecast";
import SocialMedias from "./blocks/SocialMedias";
import CustomButton from "./blocks/CustomButton";

/**
 * @param   {Boolean} readOnly
 * @returns {CompositeDecorator}
 */
function getDecoratorsForState(readOnly) {
  const decorators = [
    {
      strategy: findEntitiesByType(EntityTypeIdentifiers.LINK),
      component: Link,
      props: {
        readOnly,
      },
    },
    {
      strategy: findEntitiesByType(EntityTypeIdentifiers.MENTION),
      component: Mention,
      props: {
        readOnly,
      },
    },
  ];

  if (readOnly === false) {
    decorators.push(MentionSpanDecoratorConfig);
  }

  return new CompositeDecorator(decorators);
}

/**
 * @type {Function}
 */
const noop = () => {};

export default class GovlaunchEditor extends Component {
  static propTypes = {
    editorKey: PropTypes.string,
    readOnly: PropTypes.bool,
    editorState: PropTypes.object.isRequired,
    containerStyle: PropTypes.object,
    minContentHeight: PropTypes.number,
    autoFocus: PropTypes.bool,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    blockRendererFn: PropTypes.func,
    children: PropTypes.func,
  };

  static defaultProps = {
    editorKey: null,
    containerStyle: {},
    placeholder: null,
    blockRendererFn: noop,
    minContentHeight: null,
    readOnly: false,
    autoFocus: false,
    children: noop,
    onFocus: noop,
    onBlur: noop,
  };

  constructor(props) {
    super(props);

    this.editorKey = props.editorKey || generateRandomKey();
    this.state = {
      readOnly: props.readOnly,
    };
  }

  componentDidMount() {
    const { autoFocus } = this.props;

    if (autoFocus) {
      this.editorRef.focus();
    }
  }

  setEditorRef = (ref) => {
    this.editorRef = ref;
  };

  setWrapperRef = (ref) => {
    this.wrapperRef = ref;
  };

  getCurrentBlock = (editorState) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    return contentState.getBlockForKey(selection.getEndKey());
  };

  handleKeyBinding = (event) => {
    if (event.metaKey && event.key === "h") {
      return "highlight";
    } else if (event.key === " ") {
      // Space
      const currentBlock = this.getCurrentBlock(this.props.editorState);

      if (currentBlock.getText() === "-") {
        return "unordered-list";
      } else if (currentBlock.getText() === "1.") {
        return "ordered-list";
      }
    }

    return getDefaultKeyBinding(event);
  };

  // https://github.com/facebook/draft-js/issues/915#issuecomment-272602852
  handleKeyCommand = (command, editorState) => {
    const { onChange } = this.props;

    if (command === "highlight") {
      onChange(RichUtils.toggleInlineStyle(editorState, "HIGHLIGHT"));
      return "handled";
    }

    if (command === "unordered-list" || command === "ordered-list") {
      const currentBlock = this.getCurrentBlock(editorState);
      const currentText = currentBlock.getText();

      if (
        currentBlock.getType() === "unstyled" &&
        currentBlock.getDepth() === 0 &&
        (currentText.startsWith("-") || currentText.startsWith("1."))
      ) {
        editorState = RichUtils.toggleBlockType(editorState, `${command}-item`);

        const blockSelection = new SelectionState({
          anchorKey: currentBlock.getKey(),
          anchorOffset: 0,
          focusKey: currentBlock.getKey(),
          focusOffset: currentBlock.getLength(),
        });

        const content = Modifier.replaceText(editorState.getCurrentContent(), blockSelection, "");

        editorState = EditorState.push(editorState, content);
        editorState = EditorState.forceSelection(editorState, content.getSelectionAfter());

        onChange(editorState);
        return "handled";
      } else {
        const content = Modifier.insertText(editorState.getCurrentContent(), editorState.getSelection(), " ");

        editorState = EditorState.forceSelection(EditorState.push(editorState, content), content.getSelectionAfter());

        onChange(editorState);
        return "handled";
      }
    }

    const nextState = RichUtils.handleKeyCommand(editorState, command);

    if (nextState) {
      onChange(nextState);
      return "handled";
    }

    return "not-handled";
  };

  handleReturn = (event) => {
    const { editorState, onChange } = this.props;

    const currentBlock = this.getCurrentBlock(editorState);

    if (currentBlock.getType().endsWith("list-item") && currentBlock.getText() === "") {
      let nextState = RichUtils.toggleBlockType(editorState, currentBlock.getType());

      onChange(EditorState.forceSelection(nextState, nextState.getCurrentContent().getSelectionAfter()));

      return true;
    }

    const handleReturn = getRegisteredEvents("handleReturn");

    if (handleReturn && handleReturn(event)) {
      return true;
    }

    if (event.shiftKey) {
      const currentContent = editorState.getCurrentContent();
      const currentSelection = editorState.getSelection();
      const contentBlock = currentContent.getBlockMap().get(currentSelection.getFocusKey());

      // we'd like to allow multi-line blockquotes
      if (contentBlock.getType() === "blockquote") {
        onChange(RichUtils.insertSoftNewline(editorState));
        return true;
      }

      if (
        contentBlock.getText().charAt(currentSelection.focusOffset - 1) === "\n" ||
        contentBlock.getText().charAt(currentSelection.focusOffset) === "\n"
      ) {
        return false;
      }

      onChange(RichUtils.insertSoftNewline(editorState));
      return true;
    }

    if (currentBlock.getType().endsWith("list-item")) {
      return false;
    }

    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const endOffset = selection.getEndOffset();

    if (endOffset === currentBlock.getLength()) {
      const nextState = resetBlockStyle(editorState, selection, contentState, currentBlock, "unstyled");

      onChange(nextState);
      return true;
    }

    return false;
  };

  handleFocusEditor = () => {
    if (this.editorRef) {
      this.editorRef.focus();
    }
  };

  handleSetReadOnly = (readOnly) =>
    this.setState({
      readOnly,
    });

  render() {
    const {
      readOnly: initialReadOnly,
      editorState,
      placeholder,
      onChange,
      onBlur,
      onFocus,
      minContentHeight,
      containerStyle: containerStyleOverrides,
      blockRendererFn: actualBlockRendererFn,
      children,
    } = this.props;

    const { readOnly } = this.state;

    const ContentHeightStyle = css({
      "& .public-DraftEditor-content": {
        minHeight: minContentHeight,
      },
    });

    const modifiedState = EditorState.set(editorState, {
      decorator: getDecoratorsForState(readOnly),
    });

    return (
      <EditorAreaFocusState onFocus={onFocus} onBlur={onBlur}>
        {({ focus, bindFocus }) => (
          <div
            {...testId("Editor/rootContainer")}
            {...bindFocus}
            ref={this.setWrapperRef}
            className={classNames({
              [PlaceholderStyle]: true,
              [FigureResetStyle]: true,
              [ContentHeightStyle]: minContentHeight > 0,
            })}
            css={{
              ...containerStyle,
              ...containerStyleOverrides,
            }}
          >
            <Editor
              {...testId("Editor/root")}
              ref={this.setEditorRef}
              editorKey={this.editorKey}
              readOnly={readOnly}
              // NOTE: we're toggling `placeholder` prop based on what `shouldDisplayPlaceholder`
              // tells us. Not sure if it's the best approach - but it works nicely for not
              // displaying the placeholder under certain conditions
              placeholder={shouldDisplayPlaceholder(editorState) ? placeholder : null}
              editorState={modifiedState}
              handleReturn={this.handleReturn}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={this.handleKeyBinding}
              onChange={onChange}
              // NOTE: those handlers are actually "deprecated". We need to use `keyBindingFn` instead.
              // But, for some reason, it doesn't bubble up Up/Left/Down/Right/Esc so we don't actually
              // get the callbacks working. Let's keep those "deprecated" handlers and get rid of 'em
              // later in a future release of Draft.js
              onTab={callRegisteredEvents("onTab")}
              onUpArrow={callRegisteredEvents("onUpArrow")}
              onLeftArrow={callRegisteredEvents("onLeftArrow")}
              onDownArrow={callRegisteredEvents("onDownArrow")}
              onRightArrow={callRegisteredEvents("onRightArrow")}
              onEscape={callRegisteredEvents("onEscape")}
              stripPastedStyles={true}
              customStyleMap={{
                BOLD: {
                  fontWeight: 600,
                },
                HIGHLIGHT: {
                  backgroundColor: palette.mediumBlue,
                },
              }}
              blockRendererFn={blockRendererFn(actualBlockRendererFn, {
                readOnly: initialReadOnly,
                setReadOnly: this.handleSetReadOnly,
                editorState,
                onChange,
              })}
              blockStyleFn={(block) =>
                classNames({
                  [ParagraphStyle]: true,
                  [H3H4ParagraphStyle]: true,
                  [ResetHeadingStyle]: ["header-three", "header-four"].includes(block.getType()),
                  [H3Style]: block.getType() === "header-three",
                  [H4Style]: block.getType() === "header-four",
                  [BlockquoteStyle]: block.getType() === "blockquote",
                })
              }
            />

            {children({
              focus,
              editorKey: this.editorKey,
              editorState,
              focusEditor: this.handleFocusEditor,
              onChange,
              readOnly: this.state.readOnly,
            })}
          </div>
        )}
      </EditorAreaFocusState>
    );
  }
}

const containerStyle = {
  boxSizing: "border-box",
  position: "relative",
  cursor: "text",
};

/**
 * @param   {?Function} actualBlockRendererFn
 * @param   {Object}    blockProps
 * @returns {Function}
 */
function blockRendererFn(actualBlockRendererFn, blockProps) {
  return (block) => {
    if (actualBlockRendererFn && typeof actualBlockRendererFn === "function") {
      const actualRenderedBlock = actualBlockRendererFn(block, blockProps);

      if (actualRenderedBlock) {
        return actualRenderedBlock;
      }
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.IMAGE) {
      return {
        component: Image,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.YOUTUBE_VIDEO) {
      return {
        component: YouTubeVideo,
        editable: false,
        props: blockProps,
      };
    }

    if (
      block.getType() === "atomic" &&
      block.getData().get("blockType") === AtomicBlockIdentifiers.GOOGLE_SPREADSHEET
    ) {
      return {
        component: GoogleSpreadsheet,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.AIRTABLE) {
      return {
        component: Airtable,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.GOOGLE_FORMS) {
      return {
        component: GoogleForms,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.TYPEFORM) {
      return {
        component: Typeform,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.SIMPLECAST) {
      return {
        component: Simplecast,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.SOCIAL_MEDIAS) {
      return {
        component: SocialMedias,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.NEWSLETTER) {
      return {
        component: Newsletter,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.DIVIDER) {
      return {
        component: Divider,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.CUSTOM_BUTTON) {
      return {
        component: CustomButton,
        editable: false,
        props: blockProps,
      };
    }

    if (block.getType() === "atomic" && block.getData().get("blockType") === AtomicBlockIdentifiers.GOVLAUNCH_PROJECT) {
      return {
        component: GovlaunchProject,
        editable: false,
        props: blockProps,
      };
    }

    return null;
  };
}
